import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const testimonials = [
  {
    name: "James Mwangi",
    role: "IT Manager",
    company: "Kenya Commercial Bank",
    rating: 5,
    quote:
      "Carium Tech completely transformed our network infrastructure. Our downtime dropped significantly and security improved instantly across all 12 branches.",
  },
  {
    name: "Sarah Akinyi",
    role: "Small Business Owner",
    company: "GreenLeaf Logistics",
    rating: 5,
    quote:
      "Their CCTV installation and monitoring system is flawless. We now have full visibility across all our warehouses and delivery points.",
  },
  {
    name: "Peter Kamau",
    role: "ICT Administrator",
    company: "St. Mary's School",
    rating: 4,
    quote:
      "Professional, fast, and highly technical. They built our entire school management website and internal system from scratch. Highly recommend.",
  },
  {
    name: "Grace Wanjiku",
    role: "Founder",
    company: "TechFlow Solutions",
    rating: 5,
    quote:
      "Their cybersecurity consultation helped us identify vulnerabilities we didn't even know existed. We sleep better knowing our data is protected.",
  },
  {
    name: "David Ochieng",
    role: "Security Operations Manager",
    company: "Safepay Fintech",
    rating: 4,
    quote:
      "Reliable ISP and networking support. Their response time is unmatched — we've never had a critical issue go unresolved beyond 30 minutes.",
  },
];

function Stars({ count }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          viewBox="0 0 576 512"
          className={`w-4 h-4 ${i <= count ? "fill-yellow-400" : "fill-gray-600"}`}
        >
          <path d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z" />
        </svg>
      ))}
    </div>
  );
}

function Avatar({ name }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("");
  return (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyber-blue to-tech-green flex items-center justify-center text-black font-bold text-sm shrink-0">
      {initials}
    </div>
  );
}

const cardVariants = {
  enter: (dir) => ({ x: dir > 0 ? 200 : -200, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -200 : 200, opacity: 0 }),
};

export default function TestimonialsSection() {
  const [[current, dir], setSlide] = useState([0, 0]);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback(
    (i) => setSlide(([c]) => [i, i > c ? 1 : -1]),
    []
  );

  const next = useCallback(
    () => setSlide(([c]) => [(c + 1) % testimonials.length, 1]),
    []
  );

  const prev = useCallback(
    () =>
      setSlide(([c]) => [(c - 1 + testimonials.length) % testimonials.length, -1]),
    []
  );

  useEffect(() => {
    if (isPaused) return;
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [isPaused, next]);

  return (
    <section
      className="relative w-full py-20 lg:py-28 overflow-hidden bg-[#0a0e1a]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Liquid gradient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="liquid-blob absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
        <div className="liquid-blob absolute top-1/2 -right-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="liquid-blob absolute -bottom-40 left-1/3 w-[450px] h-[450px] rounded-full bg-sky-400/10 blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-5xl font-bold">
            What Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-tech-green">
              Clients Say
            </span>
          </h2>
          <p className="text-gray-400 text-lg mt-4 max-w-xl mx-auto">
            Trusted by businesses across East Africa to deliver secure, scalable
            technology solutions.
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="relative flex items-center gap-4">
          {/* Prev arrow */}
          <button
            onClick={prev}
            className="hidden md:flex items-center justify-center w-10 h-10 rounded-full glass shrink-0 hover:bg-white/10 transition-colors"
            aria-label="Previous"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          {/* Cards viewport */}
          <div className="overflow-hidden w-full">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={current}
                custom={dir}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6"
              >
                {/* Current card - full on mobile, 1 of 3 on desktop */}
                <TestimonialCard item={testimonials[current]} featured />

                {/* Next two cards - hidden on mobile */}
                <div className="hidden md:block">
                  <TestimonialCard
                    item={
                      testimonials[(current + 1) % testimonials.length]
                    }
                  />
                </div>
                <div className="hidden md:block">
                  <TestimonialCard
                    item={
                      testimonials[(current + 2) % testimonials.length]
                    }
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Next arrow */}
          <button
            onClick={next}
            className="hidden md:flex items-center justify-center w-10 h-10 rounded-full glass shrink-0 hover:bg-white/10 transition-colors"
            aria-label="Next"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-10">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === current
                  ? "bg-cyber-blue w-8"
                  : "bg-white/20 hover:bg-white/40"
              }`}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ item, featured }) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      className={`glass rounded-2xl p-6 lg:p-8 flex flex-col gap-4 glass-hover ${
        featured
          ? "hover:shadow-[0_0_30px_rgba(0,163,255,0.2)]"
          : "opacity-60 hover:opacity-100"
      } transition-all duration-300`}
    >
      <div className="flex items-center gap-4">
        <Avatar name={item.name} />
        <div>
          <h4 className="text-white font-bold text-sm">{item.name}</h4>
          <p className="text-gray-400 text-xs">
            {item.role}, {item.company}
          </p>
        </div>
      </div>
      <Stars count={item.rating} />
      <p className="text-gray-300 text-sm leading-relaxed">&ldquo;{item.quote}&rdquo;</p>
    </motion.div>
  );
}
